import React, { useRef, useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  IconButton,
  Typography,
  styled,
} from '@mui/material';
import {
  Close as CloseIcon,
  Image as ImageIcon,
  PictureAsPdf as PdfIcon,
  Add as AddIcon,
  Info as InfoIcon,
  AttachFile as AttachFileIcon,
  ContentPaste as ContentPasteIcon,
  ImageOutlined as ImageOutlinedIcon,
} from '@mui/icons-material';

export enum AllowedFileFormat {
  IMAGE_JPEG = 'image/jpeg',
  IMAGE_JPG = 'image/jpg',
  IMAGE_PNG = 'image/png',
  IMAGE_GIF = 'image/gif',
  IMAGE_WEBP = 'image/webp',
  IMAGE_ALL = 'image/*',
  PDF = 'application/pdf',
  PDF_EXTENSION = '.pdf',
}

const DEFAULT_MAX_FILES = 5;
const DEFAULT_MAX_FILE_SIZE = 2 * 1024 * 1024; // 2MB in bytes
const DEFAULT_ALLOWED_FORMATS = [
  AllowedFileFormat.IMAGE_JPEG,
  AllowedFileFormat.IMAGE_JPG,
  AllowedFileFormat.IMAGE_PNG,
  AllowedFileFormat.IMAGE_GIF,
  AllowedFileFormat.IMAGE_WEBP,
  AllowedFileFormat.PDF,
];

// MIME type mapping for validation
const MIME_TYPE_MAP: Record<AllowedFileFormat, string[]> = {
  [AllowedFileFormat.IMAGE_JPEG]: ['image/jpeg'],
  [AllowedFileFormat.IMAGE_JPG]: ['image/jpg'],
  [AllowedFileFormat.IMAGE_PNG]: ['image/png'],
  [AllowedFileFormat.IMAGE_GIF]: ['image/gif'],
  [AllowedFileFormat.IMAGE_WEBP]: ['image/webp'],
  [AllowedFileFormat.IMAGE_ALL]: [
    'image/jpeg',
    'image/jpg',
    'image/png',
    'image/gif',
    'image/webp',
  ],
  [AllowedFileFormat.PDF]: ['application/pdf'],
  [AllowedFileFormat.PDF_EXTENSION]: ['application/pdf'],
};

// Accept attribute mapping for file input
const ACCEPT_ATTRIBUTE_MAP: Record<AllowedFileFormat, string> = {
  [AllowedFileFormat.IMAGE_JPEG]: 'image/jpeg',
  [AllowedFileFormat.IMAGE_JPG]: 'image/jpeg',
  [AllowedFileFormat.IMAGE_PNG]: 'image/png',
  [AllowedFileFormat.IMAGE_GIF]: 'image/gif',
  [AllowedFileFormat.IMAGE_WEBP]: 'image/webp',
  [AllowedFileFormat.IMAGE_ALL]: 'image/*',
  [AllowedFileFormat.PDF]: 'application/pdf',
  [AllowedFileFormat.PDF_EXTENSION]: '.pdf',
};

export interface AttachmentFile {
  file: File;
  preview?: string;
  id: string;
}

interface AttachmentInputMAIQProps {
  attachments: AttachmentFile[];
  onAttachmentsChange: (attachments: AttachmentFile[]) => void;
  disabled?: boolean;
  maxAttachments?: number;
  allowedFileFormats?: AllowedFileFormat[];
  maxFileSize?: number; // in bytes
  showGuidelines?: boolean;
  guidelinesTitle?: string;
  quickTipText?: string;
  highlightedFormat?: string; // Format to highlight (e.g., 'PDF')
}

const AttachmentContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: theme.spacing(1.5),
  marginTop: theme.spacing(2),
}));

// Square box container for each attachment/empty slot
const AttachmentBox = styled(Box)<{ isEmpty?: boolean; isDragOver?: boolean }>(
  ({ theme, isEmpty, isDragOver }) => ({
    position: 'relative',
    width: '120px',
    height: '120px',
    borderRadius: theme.shape.borderRadius,
    border: isEmpty
      ? `2px dashed ${theme.palette.divider}`
      : `1px solid ${theme.palette.divider}`,
    backgroundColor: isEmpty
      ? theme.palette.action.hover
      : theme.palette.background.paper,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: isEmpty && !isDragOver ? 'pointer' : 'default',
    transition: 'all 0.2s ease-in-out',
    overflow: 'hidden',
    '&:hover': {
      borderColor: isEmpty ? theme.palette.primary.main : theme.palette.divider,
      backgroundColor: isEmpty
        ? theme.palette.action.selected
        : theme.palette.background.paper,
    },
    ...(isDragOver && {
      borderColor: theme.palette.primary.main,
      backgroundColor: theme.palette.action.selected,
      borderWidth: '2px',
      borderStyle: 'dashed',
    }),
  })
);

const PreviewImage = styled('img')({
  width: '100%',
  height: '100%',
  objectFit: 'cover',
});

const EmptyBoxContent = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: theme.spacing(0.5),
  color: theme.palette.text.secondary,
}));

const EmptyBoxIcon = styled(AddIcon)(({ theme }) => ({
  fontSize: '32px',
  color: theme.palette.text.secondary,
}));

const EmptyBoxText = styled(Typography)(({ theme }) => ({
  fontSize: '0.75rem',
  color: theme.palette.text.secondary,
  textAlign: 'center',
}));

const FileInfoOverlay = styled(Box)(({ theme }) => ({
  position: 'absolute',
  bottom: 0,
  left: 0,
  right: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.7)',
  color: theme.palette.common.white,
  padding: theme.spacing(0.5),
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(0.25),
}));

const FileNameOverlay = styled(Typography)({
  fontSize: '0.6875rem',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  fontWeight: 500,
});

const FileSizeOverlay = styled(Typography)({
  fontSize: '0.625rem',
  opacity: 0.9,
});

const PdfIconContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  width: '100%',
  height: '100%',
  gap: theme.spacing(0.5),
  backgroundColor: theme.palette.background.default,
}));

const PdfIconWrapper = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
});

const RemoveButton = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  top: 4,
  right: 4,
  padding: theme.spacing(0.25),
  backgroundColor: 'rgba(0, 0, 0, 0.6)',
  color: theme.palette.common.white,
  width: 24,
  height: 24,
  minWidth: 24,
  minHeight: 24,
  zIndex: 1,
  '&:hover': {
    backgroundColor: 'rgba(0, 0, 0, 0.8)',
    color: theme.palette.common.white,
  },
  '& .MuiSvgIcon-root': {
    fontSize: 16,
  },
}));

// Guidelines styled components
const GuidelinesContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  borderRadius: theme.shape.borderRadius,
  border: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.paper,
  marginBottom: theme.spacing(2),
}));

const GuidelinesTitle = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  marginBottom: theme.spacing(1.5),
}));

const GuidelinesTitleText = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  fontSize: '0.875rem',
  color: theme.palette.text.primary,
}));

const GuidelinesItem = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  gap: theme.spacing(1),
  marginBottom: theme.spacing(1),
  '&:last-child': {
    marginBottom: 0,
  },
}));

const GuidelinesItemText = styled(Typography)(({ theme }) => ({
  fontSize: '0.75rem',
  color: theme.palette.text.primary,
  lineHeight: 1.5,
  flex: 1,
}));

const KeyboardKey = styled(Box)(({ theme }) => ({
  display: 'inline-block',
  padding: '2px 6px',
  borderRadius: '4px',
  backgroundColor: theme.palette.mode === 'dark' ? '#3a3a3a' : '#e0e0e0',
  color: theme.palette.text.primary,
  fontSize: '0.6875rem',
  fontWeight: 500,
  fontFamily: 'monospace',
  margin: '0 2px',
}));

const FormatTag = styled(Box)<{ isHighlighted?: boolean }>(({ theme, isHighlighted }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: theme.spacing(0.5),
  padding: '4px 10px',
  borderRadius: '16px',
  border: `1px solid ${isHighlighted ? theme.palette.error.main : theme.palette.divider}`,
  backgroundColor: isHighlighted
    ? theme.palette.error.main
    : theme.palette.mode === 'dark'
    ? 'rgba(255, 255, 255, 0.05)'
    : 'rgba(0, 0, 0, 0.05)',
  color: isHighlighted ? theme.palette.common.white : theme.palette.text.primary,
  fontSize: '0.6875rem',
  fontWeight: 500,
  marginRight: theme.spacing(0.5),
  marginBottom: theme.spacing(0.5),
}));

const FormatTagIcon = styled(Box)({
  display: 'inline-flex',
  alignItems: 'center',
  '& .MuiSvgIcon-root': {
    fontSize: '0.875rem',
  },
});

export const AttachmentInputMAIQ: React.FC<AttachmentInputMAIQProps> = ({
  attachments,
  onAttachmentsChange,
  disabled = false,
  maxAttachments = DEFAULT_MAX_FILES,
  allowedFileFormats = DEFAULT_ALLOWED_FORMATS,
  maxFileSize = DEFAULT_MAX_FILE_SIZE,
  showGuidelines = true,
  guidelinesTitle = 'File Attachment Guidelines',
  quickTipText = 'You can paste screenshots directly using',
  highlightedFormat = 'PDF',
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

  // Get all valid MIME types from allowed formats
  const validMimeTypes = useMemo(() => {
    const mimeTypes = new Set<string>();
    allowedFileFormats.forEach((format) => {
      MIME_TYPE_MAP[format]?.forEach((mime) => mimeTypes.add(mime));
    });
    return Array.from(mimeTypes);
  }, [allowedFileFormats]);

  // Get accept attribute string for file input
  const acceptAttribute = useMemo(() => {
    const acceptValues = allowedFileFormats.map(
      (format) => ACCEPT_ATTRIBUTE_MAP[format]
    );
    return Array.from(new Set(acceptValues)).join(',');
  }, [allowedFileFormats]);

  // Get formatted list of allowed formats for error messages
  const allowedFormatsText = useMemo(() => {
    const formats = new Set<string>();
    allowedFileFormats.forEach((format) => {
      if (format === AllowedFileFormat.IMAGE_ALL) {
        formats.add('images');
      } else if (format === AllowedFileFormat.PDF || format === AllowedFileFormat.PDF_EXTENSION) {
        formats.add('PDF');
      } else {
        const mimeTypes = MIME_TYPE_MAP[format];
        if (mimeTypes) {
          mimeTypes.forEach((mime) => {
            if (mime.startsWith('image/')) {
              formats.add('images');
            } else if (mime === 'application/pdf') {
              formats.add('PDF');
            }
          });
        }
      }
    });
    return Array.from(formats).join(' and ');
  }, [allowedFileFormats]);

  const validateFile = useCallback(
    (file: File): string | null => {
      // Check file size
      if (file.size > maxFileSize) {
        const maxSizeMB = (maxFileSize / 1024 / 1024).toFixed(2);
        return `File "${file.name}" exceeds ${maxSizeMB}MB size limit`;
      }

      // Check file type
      const isValidType =
        validMimeTypes.some((type) => file.type === type) ||
        (allowedFileFormats.includes(AllowedFileFormat.IMAGE_ALL) &&
          file.type.startsWith('image/')) ||
        ((allowedFileFormats.includes(AllowedFileFormat.PDF) ||
          allowedFileFormats.includes(AllowedFileFormat.PDF_EXTENSION)) &&
          file.name.toLowerCase().endsWith('.pdf'));

      if (!isValidType) {
        return `File "${file.name}" is not a supported format. Only ${allowedFormatsText} files are allowed.`;
      }

      return null;
    },
    [maxFileSize, validMimeTypes, allowedFileFormats, allowedFormatsText]
  );

  const createPreview = (file: File): Promise<string | undefined> => {
    return new Promise((resolve) => {
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = (e) => {
          resolve(e.target?.result as string);
        };
        reader.onerror = () => resolve(undefined);
        reader.readAsDataURL(file);
      } else {
        resolve(undefined);
      }
    });
  };

  const handleFileSelect = useCallback(
    async (files: FileList | null) => {
      if (!files || files.length === 0 || disabled) return;

      const currentCount = attachments.length;
      const remainingSlots = maxAttachments - currentCount;

      if (remainingSlots <= 0) {
        alert(`Maximum ${maxAttachments} attachments allowed`);
        return;
      }

      const filesToAdd: File[] = Array.from(files).slice(0, remainingSlots);
      const newAttachments: AttachmentFile[] = [];

      for (const file of filesToAdd) {
        const error = validateFile(file);
        if (error) {
          alert(error);
          continue;
        }

        const preview = await createPreview(file);
        newAttachments.push({
          file,
          preview,
          id: `${Date.now()}-${Math.random()}`,
        });
      }

      if (newAttachments.length > 0) {
        // Always add new attachments (new empty box will appear automatically)
        onAttachmentsChange([...attachments, ...newAttachments]);
      }

      // Reset input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    },
    [attachments, onAttachmentsChange, disabled, maxAttachments, validateFile]
  );

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleFileSelect(e.target.files);
  };

  const handleRemove = (id: string) => {
    onAttachmentsChange(attachments.filter((att) => att.id !== id));
  };

  const handleEmptyBoxClick = () => {
    if (!disabled && attachments.length < maxAttachments) {
      fileInputRef.current?.click();
    }
  };

  const handleDrop = useCallback(
    async (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setDragOverIndex(null);

      if (disabled) return;

      const files = e.dataTransfer.files;
      if (files && files.length > 0) {
        // Always add new attachments (empty box will appear automatically)
        await handleFileSelect(files);
      }
    },
    [disabled, handleFileSelect]
  );

  // Handle paste event for screenshots - paste into first empty box
  useEffect(() => {
    const handlePaste = async (e: ClipboardEvent) => {
      if (disabled || attachments.length >= maxAttachments) return;

      const items = e.clipboardData?.items;
      if (!items) return;

      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        if (
          item.type.indexOf('image') !== -1 &&
          (allowedFileFormats.includes(AllowedFileFormat.IMAGE_ALL) ||
            validMimeTypes.some((mime) => item.type === mime))
        ) {
          e.preventDefault();
          const blob = item.getAsFile();
          if (blob) {
            const file = new File([blob], `screenshot-${Date.now()}.png`, {
              type: 'image/png',
            });
            // Create a FileList-like object using DataTransfer
            const dataTransfer = new DataTransfer();
            dataTransfer.items.add(file);
            // Paste into the first empty slot
            await handleFileSelect(dataTransfer.files);
          }
          break;
        }
      }
    };

    document.addEventListener('paste', handlePaste);
    return () => {
      document.removeEventListener('paste', handlePaste);
    };
  }, [disabled, attachments.length, handleFileSelect, maxAttachments, allowedFileFormats, validMimeTypes]);

  const getFileIcon = (file: File) => {
    if (file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf')) {
      return <PdfIcon sx={{ fontSize: 20 }} />;
    }
    return <ImageIcon sx={{ fontSize: 20 }} />;
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Get format display names for guidelines
  const getFormatDisplayNames = useMemo(() => {
    const formatMap: Record<string, string> = {
      [AllowedFileFormat.IMAGE_JPEG]: 'JPEG',
      [AllowedFileFormat.IMAGE_JPG]: 'JPG',
      [AllowedFileFormat.IMAGE_PNG]: 'PNG',
      [AllowedFileFormat.IMAGE_GIF]: 'GIF',
      [AllowedFileFormat.IMAGE_WEBP]: 'WEBP',
      [AllowedFileFormat.PDF]: 'PDF',
      [AllowedFileFormat.PDF_EXTENSION]: 'PDF',
    };

    const formats: string[] = [];
    const hasImageAll = allowedFileFormats.includes(AllowedFileFormat.IMAGE_ALL);
    const hasImages = hasImageAll || allowedFileFormats.some(
      (f) =>
        f === AllowedFileFormat.IMAGE_JPEG ||
        f === AllowedFileFormat.IMAGE_JPG ||
        f === AllowedFileFormat.IMAGE_PNG ||
        f === AllowedFileFormat.IMAGE_GIF ||
        f === AllowedFileFormat.IMAGE_WEBP
    );
    const hasPDF = allowedFileFormats.some(
      (f) => f === AllowedFileFormat.PDF || f === AllowedFileFormat.PDF_EXTENSION
    );

    if (hasImages) {
      formats.push('Images');
      // Add individual image formats
      if (hasImageAll) {
        formats.push('JPEG', 'JPG', 'PNG', 'GIF', 'WEBP');
      } else {
        // Add only the specific formats that are allowed
        const addedFormats = new Set<string>();
        allowedFileFormats.forEach((format) => {
          const displayName = formatMap[format];
          if (displayName && displayName !== 'PDF' && !addedFormats.has(displayName)) {
            formats.push(displayName);
            addedFormats.add(displayName);
          }
        });
      }
    }
    if (hasPDF) {
      formats.push('PDF');
    }

    return formats;
  }, [allowedFileFormats]);

  return (
    <Box>
      <input
        ref={fileInputRef}
        type="file"
        accept={acceptAttribute}
        multiple
        onChange={handleFileInputChange}
        style={{ display: 'none' }}
        disabled={disabled}
      />

      {showGuidelines && (
        <GuidelinesContainer>
          <GuidelinesTitle>
            <InfoIcon sx={{ fontSize: '1rem', color: 'text.secondary' }} />
            <GuidelinesTitleText>{guidelinesTitle}</GuidelinesTitleText>
          </GuidelinesTitle>

          <GuidelinesItem>
            <ContentPasteIcon sx={{ fontSize: '0.875rem', color: 'text.secondary', mt: 0.25 }} />
            <GuidelinesItemText>
              <strong>Quick tip:</strong> {quickTipText}{' '}
              <KeyboardKey>Ctrl+V</KeyboardKey> or <KeyboardKey>Cmd+V</KeyboardKey>
            </GuidelinesItemText>
          </GuidelinesItem>

          <GuidelinesItem>
            <AttachFileIcon sx={{ fontSize: '0.875rem', color: 'text.secondary', mt: 0.25 }} />
            <GuidelinesItemText>
              <strong>Maximum {maxAttachments} files</strong> can be attached per submission
            </GuidelinesItemText>
          </GuidelinesItem>

          <GuidelinesItem>
            <ImageOutlinedIcon sx={{ fontSize: '0.875rem', color: 'text.secondary', mt: 0.25 }} />
            <GuidelinesItemText>
              Each file must be <strong>{formatFileSize(maxFileSize)} or less</strong> in size
            </GuidelinesItemText>
          </GuidelinesItem>

          <GuidelinesItem>
            <ImageOutlinedIcon sx={{ fontSize: '0.875rem', color: 'text.secondary', mt: 0.25 }} />
            <GuidelinesItemText>
              <strong>Allowed formats:</strong>
              <Box sx={{ display: 'inline-block', ml: 0.5, mt: 0.5 }}>
                {getFormatDisplayNames.map((format, index) => {
                  const isHighlighted = format.toUpperCase() === highlightedFormat.toUpperCase();
                  const isFirstImage = format === 'Images' && index === 0;
                  const isPDF = format === 'PDF';
                  return (
                    <FormatTag key={format} isHighlighted={isHighlighted}>
                      {isFirstImage && (
                        <FormatTagIcon>
                          <ImageOutlinedIcon sx={{ fontSize: '0.75rem' }} />
                        </FormatTagIcon>
                      )}
                      {isPDF && (
                        <FormatTagIcon>
                          <PdfIcon sx={{ fontSize: '0.75rem' }} />
                        </FormatTagIcon>
                      )}
                      {format}
                    </FormatTag>
                  );
                })}
              </Box>
            </GuidelinesItemText>
          </GuidelinesItem>
        </GuidelinesContainer>
      )}

      <AttachmentContainer>
        {/* Render existing attachments */}
        {attachments.map((attachment, index) => (
          <AttachmentBox
            key={attachment.id}
            onDragOver={(e: React.DragEvent) => {
              e.preventDefault();
              e.stopPropagation();
              if (!disabled) {
                setDragOverIndex(index);
              }
            }}
            onDragLeave={(e: React.DragEvent) => {
              e.preventDefault();
              e.stopPropagation();
              setDragOverIndex(null);
            }}
            onDrop={handleDrop}
            isDragOver={dragOverIndex === index}
          >
            {attachment.preview ? (
              <>
                <PreviewImage src={attachment.preview} alt={attachment.file.name} />
                <FileInfoOverlay>
                  <FileNameOverlay title={attachment.file.name}>
                    {attachment.file.name}
                  </FileNameOverlay>
                  <FileSizeOverlay>{formatFileSize(attachment.file.size)}</FileSizeOverlay>
                </FileInfoOverlay>
              </>
            ) : (
              <>
                <PdfIconContainer>
                  <PdfIconWrapper>
                    {getFileIcon(attachment.file)}
                  </PdfIconWrapper>
                  <FileInfoOverlay>
                    <FileNameOverlay title={attachment.file.name}>
                      {attachment.file.name}
                    </FileNameOverlay>
                    <FileSizeOverlay>{formatFileSize(attachment.file.size)}</FileSizeOverlay>
                  </FileInfoOverlay>
                </PdfIconContainer>
              </>
            )}
            <RemoveButton
              size="small"
              onClick={() => handleRemove(attachment.id)}
              disabled={disabled}
              aria-label="Remove attachment"
            >
              <CloseIcon />
            </RemoveButton>
          </AttachmentBox>
        ))}

        {/* Render empty box if space available */}
        {attachments.length < maxAttachments && (
          <AttachmentBox
            isEmpty
            onClick={handleEmptyBoxClick}
            onDragOver={(e: React.DragEvent) => {
              e.preventDefault();
              e.stopPropagation();
              if (!disabled) {
                setDragOverIndex(attachments.length);
              }
            }}
            onDragLeave={(e: React.DragEvent) => {
              e.preventDefault();
              e.stopPropagation();
              setDragOverIndex(null);
            }}
            onDrop={handleDrop}
            isDragOver={dragOverIndex === attachments.length}
          >
            <EmptyBoxContent>
              <EmptyBoxIcon />
              <EmptyBoxText>Drop or click</EmptyBoxText>
              <EmptyBoxText sx={{ fontSize: '0.625rem' }}>
                {attachments.length}/{maxAttachments}
              </EmptyBoxText>
            </EmptyBoxContent>
          </AttachmentBox>
        )}
      </AttachmentContainer>
    </Box>
  );
};
